import CloseIcon from '@mui/icons-material/Close';
import { SxProps, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React from 'react';

import useResponsive from '@/hooks/useResponsive';

interface ModalProps {
  isOpen: boolean;
  title?: React.ReactNode | string;
  halfTitle?: string;
  titleMediumSize?: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  customStyles?: React.CSSProperties;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  scroll?: 'body' | 'paper';
  sx?: SxProps<Theme>;
  onTransitionExited?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  actions,
  customStyles,
  titleMediumSize,
  maxWidth = 'sm',
  scroll = 'body',
  sx,
  onTransitionExited,
}) => {
  const isMobile = useResponsive('down', 'sm');
  const theme = useTheme();
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      scroll={scroll}
      sx={sx}
      slotProps={{
        transition: { onExited: onTransitionExited },
        paper: { sx: { maxHeight: scroll === 'paper' ? '90vh' : undefined } },
      }}
    >
      <Box
        sx={{
          color: theme.palette.secondary.main,
          textAlign: 'center',
          minHeight: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem 0',
          ...customStyles,
        }}
      >
        <Typography
          variant={titleMediumSize ? 'subtitle1' : 'h5'}
          sx={{ width: isMobile ? '65%' : '80%', span: { color: theme.palette.primary.main } }}
        >
          {title}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} style={{ position: 'absolute', right: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider variant="fullWidth" sx={{ width: '100%' }} />

      <DialogContent
        sx={{
          overflowY: scroll === 'paper' ? 'auto' : 'visible',
          overflowX: 'hidden',
          ...(scroll === 'paper' && { minHeight: '200px' }),
          position: 'relative',
        }}
      >
        {children}
      </DialogContent>
      {actions && <DialogActions sx={{ padding: '0 1rem 1rem' }}>{actions}</DialogActions>}
    </Dialog>
  );
};

export default Modal;
